// Environment configuration for API endpoints and services
export interface EnvironmentConfig {
  // API Base URLs
  apiBaseUrl: string;

  // Visibility flags
  showLogin: boolean;
  hideCalendly: boolean;

  // Feature Flags
  features: {
    aiAssessment: boolean;
    meetingIntelligence: boolean;
    realTimeTranscription: boolean;
    videoAnalysis: boolean;
    clientDetailV2: boolean;
  };
  
  // Environment Info
  environment: 'local' | 'dev' | 'production';
  debug: boolean;
}

// Default configuration
const defaultConfig: EnvironmentConfig = {
  apiBaseUrl: process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api',
  showLogin: process.env.REACT_APP_SHOW_LOGIN !== 'false',
  hideCalendly: process.env.REACT_APP_HIDE_CALENDLY !== 'false',  // default true

  features: {
    aiAssessment: false,
    meetingIntelligence: false,
    realTimeTranscription: false,
    videoAnalysis: false,
    clientDetailV2: process.env.REACT_APP_CLIENT_DETAIL_V2 === 'true',
  },
  
  environment: (process.env.NODE_ENV as 'local' | 'dev' | 'production') || 'local',
  debug: process.env.REACT_APP_DEBUG === 'true',
};

// Environment-specific overrides
const environmentOverrides: Record<string, Partial<EnvironmentConfig>> = {
  local: {
    debug: true,
  },
  dev: {
    debug: true,
  },
  production: {
    debug: false,
  },
};

// Merge configuration with environment-specific overrides
export const config: EnvironmentConfig = {
  ...defaultConfig,
  ...(environmentOverrides[defaultConfig.environment] || {}),
};

// Validation function
export const validateConfig = (): void => {
  const requiredEnvVars = [
    'REACT_APP_API_BASE_URL',
  ];
  
  const missingVars = requiredEnvVars.filter(varName => !process.env[varName]);
  
  if (missingVars.length > 0 && config.environment === 'production') {
    console.error('Missing required environment variables:', missingVars);
    throw new Error(`Missing required environment variables: ${missingVars.join(', ')}`);
  }
  
  if (config.debug) {
    console.log('Environment Configuration:', {
      environment: config.environment,
      apiBaseUrl: config.apiBaseUrl,
      features: config.features,
    });
  }
};

// Initialize configuration validation
validateConfig();

export default config;
